import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Calendar, CheckCircle2 } from "lucide-react";

const BookDemoModal = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        company: "",
        teamSize: "",
        message: "",
    });
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Mock submission
        console.log("Demo request submitted:", formData);
        setIsSubmitted(true);

        // Reset after 3 seconds
        setTimeout(() => {
            setIsSubmitted(false);
            setFormData({
                fullName: "",
                email: "",
                company: "",
                teamSize: "",
                message: "",
            });
            onClose();
        }, 3000);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                {!isSubmitted ? (
                    <>
                        <DialogHeader>
                            <DialogTitle className="text-2xl font-bold text-slate-900">
                                Book Your Demo
                            </DialogTitle>
                            <DialogDescription className="text-slate-600">
                                See how Nucleus can save your team 6+ hours
                                every week. We'll show you a personalized demo
                                tailored to your needs.
                            </DialogDescription>
                        </DialogHeader>

                        <form
                            onSubmit={handleSubmit}
                            className="space-y-5 mt-4"
                        >
                            <div>
                                <Label
                                    htmlFor="fullName"
                                    className="text-slate-900 font-semibold"
                                >
                                    Full Name *
                                </Label>
                                <Input
                                    id="fullName"
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={handleInputChange}
                                    placeholder="John Doe"
                                    required
                                    className="mt-2"
                                />
                            </div>

                            <div>
                                <Label
                                    htmlFor="email"
                                    className="text-slate-900 font-semibold"
                                >
                                    Work Email *
                                </Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="john@company.com"
                                    required
                                    className="mt-2"
                                />
                            </div>

                            <div>
                                <Label
                                    htmlFor="company"
                                    className="text-slate-900 font-semibold"
                                >
                                    Company *
                                </Label>
                                <Input
                                    id="company"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleInputChange}
                                    placeholder="Acme Inc."
                                    required
                                    className="mt-2"
                                />
                            </div>

                            <div>
                                <Label
                                    htmlFor="teamSize"
                                    className="text-slate-900 font-semibold"
                                >
                                    Engineering Team Size *
                                </Label>
                                <Input
                                    id="teamSize"
                                    name="teamSize"
                                    value={formData.teamSize}
                                    onChange={handleInputChange}
                                    placeholder="e.g., 10-50"
                                    required
                                    className="mt-2"
                                />
                            </div>

                            <div>
                                <Label
                                    htmlFor="message"
                                    className="text-slate-900 font-semibold"
                                >
                                    What are you looking to solve? (Optional)
                                </Label>
                                <Textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    placeholder="Tell us about your current challenges..."
                                    className="mt-2 min-h-24"
                                />
                            </div>

                            <Button
                                type="submit"
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-semibold"
                            >
                                <Calendar className="w-5 h-5 mr-2" />
                                Schedule Demo
                            </Button>

                            <p className="text-xs text-slate-500 text-center">
                                We'll reach out within 24 hours to schedule a
                                time that works for you.
                            </p>
                        </form>
                    </>
                ) : (
                    <div className="py-12 text-center">
                        <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle2 className="w-10 h-10 text-teal-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-3">
                            Demo Request Received!
                        </h3>
                        <p className="text-slate-600 mb-2">
                            Thank you for your interest in Nucleus.
                        </p>
                        <p className="text-slate-600">
                            We'll contact you within 24 hours to schedule your
                            personalized demo.
                        </p>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default BookDemoModal;
